/**
 * DIVISION CONFIGURATION DIALOG
 * Phase 2 UI Component - Step 2
 *
 * This component allows editing Division-level configuration:
 * - Personnel count (Division HQ)
 * - Operational state
 * - Position from FEBA
 * - Phase inheritance toggle (Corps vs Custom)
 * - Custom operational phases (when not inheriting)
 * - Per-phase overrides when inheriting (ENH-023)
 *
 * INTEGRATION: Add this component to Tab2_Operations.jsx
 *
 * Date: January 30, 2026
 * For: Dr. Tatar Radu MD - PSP/CRE Tool v3.0
 *
 * Version: 1.5.0 (April 10, 2026) - V4-013: Sub-phase Division Tactical Differentiation.
 *   Mirror of BrigadePhaseTab v1.6.0 at division level. Division phases (inherited from corps
 *   or custom) can be split into sub-phases with independent mission_type and terrain override.
 *   Stored in phase_overrides[idx].sub_phases (inherited) or phases[idx].sub_phases (custom).
 *   buildEffectivePhases() propagates sub_phases into the effective phases dict passed to brigades.
 *   UI: ExpandMoreIcon toggle in Actions column; Collapse panel with SP rows.
 * Version: 1.4.0 (March 20, 2026) - ENH-023: Per-phase override for inherited phases.
 *   Symmetric implementation to BrigadePhaseTab v1.5.0 at division level.
 *   New state: phaseOverrides (0-based index → {operational_posture, mission_type, description}).
 *   Duration override not permitted. UI: per-row Override/Reset in inherited corps-phase table;
 *   overridden rows highlighted amber. handleSave passes both phases (effective) and
 *   phase_overrides (raw dict) so Tab2 cascade can re-apply overrides after corps changes.
 *   DialogTitle chip shows override count when partially overriding.
 * Version: 1.3.0 (March 18, 2026) - APP_RULES: replace all bare emoji with icons.js constants.
 *   🪖 → MILITARY_ICONS.BATTALION (×2)  |  📚 → STATUS_ICONS.INFO
 *   ✅ → STATUS_ICONS.GREEN_CHECK (×2)  |  ⚠️ → STATUS_ICONS.YELLOW_WARNING
 *   ❌ → STATUS_ICONS.RED_X             |  ⚙️ → UI_ICONS.SETTINGS
 *   Imports added: MILITARY_ICONS, STATUS_ICONS, UI_ICONS from utils/icons.js
 * Version: 1.2.0 (March 13, 2026) - BUG-090: Grid v1 → v2 migration in Division HQ Configuration block.
 *   Three <Grid item xs={12} md={4}> → <Grid size={{ xs: 12, md: 4 }}> (lines 259, 274, 289).
 * Version: 1.1.0 (March 13, 2026) - BUG-076: Posture/mission auto-sync in phase editing.
 *   updatePhase() now auto-sets mission_type when operational_posture changes. Division phase
 *   mismatch warnings appear in this dialog (BUG-077 companion) — shows only this division's
 *   custom-phase mismatches, with "Fix All" button.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  AlertTitle,
  Stack,
  Box,
  Grid,
  Collapse,
  InputAdornment,
  Divider,
  FormControlLabel,
  Switch,
  Tooltip,
  ListSubheader
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import {
  MILITARY_ICONS,
  STATUS_ICONS,
  UI_ICONS,
} from '../utils/icons.js';
// eslint-disable-next-line no-unused-vars
import { OPERATION_TYPES, migrateSubPhase } from '../constants/missions.js';
import PhaseCasualtyControls from './PhaseCasualtyControls';
import { resolvePhaseProfile } from '../constants/phaseCasualtyTable';

/**
 * Division Configuration Dialog Component
 *
 * @param {boolean}  open                  - Dialog open state
 * @param {Function} onClose               - Close handler
 * @param {Function} onSave                - Save handler (receives division ID and complete config)
 * @param {Object}   initialDivision       - Current Division configuration
 * @param {Array}    corpsPhases           - Corps phases (for inheritance display)
 * @param {number}   operationDurationDays - Total operation duration
 * @param {Array}    brigades              - Brigades in this division
 * @param {Function} onToggleInheritance   - Handler for inheritance toggle
 */
const DivisionConfigDialog = ({
  open,
  onClose,
  onSave,
  initialDivision = null,
  corpsPhases = [],
  operationDurationDays = 10,
  brigades = [],
  onToggleInheritance
}) => {

  // Local state for editing
  const [personnel,        setPersonnel]        = useState(initialDivision?.personnel || 1000);
  const [operationalState, setOperationalState] = useState(initialDivision?.operational_state || 'SUPPORT_ACTIVE');
  const [positionFeba,     setPositionFeba]     = useState(initialDivision?.position_feba_km || 30);
  const [usesCorpsPhases,  setUsesCorpsPhases]  = useState(initialDivision?.uses_corps_phases !== false);
  const [phases,           setPhases]           = useState(initialDivision?.phases || []);
  // ENH-023: per-phase overrides — 0-based index → {operational_posture, mission_type, description}
  const [phaseOverrides,   setPhaseOverrides]   = useState(initialDivision?.phase_overrides || {});
  // V4-013: tracks which phase rows have expanded sub-phase panel
  const [expandedSubPhases, setExpandedSubPhases] = useState({});

  // Reset state when dialog opens with new division
  useEffect(() => {
    if (open && initialDivision) {
      setPersonnel(initialDivision.personnel || 1000);
      setOperationalState(initialDivision.operational_state || 'SUPPORT_ACTIVE');
      setPositionFeba(initialDivision.position_feba_km || 30);
      setUsesCorpsPhases(initialDivision.uses_corps_phases !== false);
      setPhases(initialDivision.phases || []);
      setPhaseOverrides(initialDivision.phase_overrides || {});
      setExpandedSubPhases({});
    }
  }, [open, initialDivision]);

  // Calculate phase days from durations
  const calculatePhaseDays = (phasesArray) => {
    let currentDay = 1;
    return phasesArray.map(phase => {
      const startDay = currentDay;
      const endDay = currentDay + phase.duration_days - 1;
      currentDay = endDay + 1;
      return { ...phase, start_day: startDay, end_day: endDay };
    });
  };

  // BUG-076 FIX (March 13, 2026): posture → mission auto-sync.
  const POSTURE_TO_MISSION = {
    ATTACK:                'ATTACK',
    HASTY_ATTACK:          'ATTACK',
    DELIBERATE_ATTACK:     'ATTACK',
    EXPLOITATION:          'EXPLOITATION',
    PURSUIT:               'ATTACK',
    MOVEMENT_TO_CONTACT:   'ATTACK',
    MOBILE_DEFENSE:        'DEFEND',
    RETIREMENT:            'CONSOLIDATION',
    DEFENSE:               'DEFEND',
    HASTY_DEFENSE:         'DEFEND',
    DELIBERATE_DEFENSE:    'DEFEND',
    DELAY:                 'DEFEND',
    WITHDRAWAL:            'CONSOLIDATION',
    RETIRE:                'CONSOLIDATION',
    RESERVE:               'RESERVE',
    MARCH:                 'MOVEMENT',
    MARCH_TACTICAL:        'MOVEMENT',
    MARCH_ADMINISTRATIVE:  'MOVEMENT',
    MARCHING:              'MOVEMENT',
    TRANSIT:               'MOVEMENT',
    RECONSTITUTION:        'SUPPORTING',
    SUPPORT_OPERATIONS:    'SUPPORTING',
    CONSOLIDATION:         'CONSOLIDATION'
  };

  // V5-013: Division role chip options for per-phase corps-level role designation.
  // Only shown when corpsPhases.length > 0 (division is under a corps echelon).
  // Calculator reads division_configs[j].phase_overrides[phaseIndex].division_role.
  // MAIN_EFFORT=×1.15, ECONOMY_OF_FORCE=×0.70, SUPPORTING=×1.0 (no change).
  const DIVISION_ROLE_OPTIONS = [
    { value: 'MAIN_EFFORT',      label: 'Main Effort ×1.15',  color: 'error'   },
    { value: 'SUPPORTING',       label: 'Supporting ×1.0',    color: 'primary' },
    { value: 'ECONOMY_OF_FORCE', label: 'Economy ×0.70',      color: 'info'    },
  ];

  // ── Custom-phase handlers ──────────────────────────────────────────────────

  const updatePhase = (phaseIndex, field, value) => {
    const newPhases = [...phases];
    const updatedPhase = { ...newPhases[phaseIndex], [field]: value };
    if (field === 'operational_posture') {
      updatedPhase.mission_type = POSTURE_TO_MISSION[value] || 'DEFEND';
    }
    newPhases[phaseIndex] = updatedPhase;
    setPhases(calculatePhaseDays(newPhases));
  };

  const addPhase = () => {
    const newPhase = {
      phase_number: phases.length + 1,
      duration_days: 1,
      operational_posture: 'DELIBERATE_DEFENSE',
      mission_type: 'DEFEND',
      description: `Phase ${phases.length + 1}`
    };
    setPhases(calculatePhaseDays([...phases, newPhase]));
  };

  const deletePhase = (phaseIndex) => {
    const newPhases = phases.filter((_, i) => i !== phaseIndex);
    const renumbered = newPhases.map((p, i) => ({ ...p, phase_number: i + 1 }));
    setPhases(calculatePhaseDays(renumbered));
  };

  // Handle inheritance toggle
  const handleToggleInheritance = (checked) => {
    setUsesCorpsPhases(checked);
    if (checked) {
      // Switching TO inherit — copy current Corps phases as local phases base
      setPhases(calculatePhaseDays(corpsPhases.map(p => ({ ...p }))));
    } else {
      // Switching TO custom — keep current phases or copy Corps as starting point
      if (phases.length === 0) {
        setPhases(calculatePhaseDays(corpsPhases.map(p => ({ ...p }))));
      }
    }
  };

  // ── ENH-023: Per-phase override handlers ──────────────────────────────────

  /**
   * Build effective phases: corps phases with division overrides applied.
   * Duration is never overridden — corps timeline structure is preserved.
   */
  const buildEffectivePhases = (parentPhases, overrides) =>
    parentPhases.map((phase, index) => {
      const ov = overrides[index];
      if (!ov) return { ...phase };
      return {
        ...phase,
        operational_posture: ov.operational_posture ?? phase.operational_posture,
        mission_type:        ov.mission_type        ?? phase.mission_type,
        description:         ov.description         ?? phase.description,
        // 2026-10-13 (Tab 2 plan, step 3b): casualty profile of the phase (constants/phaseCasualtyTable.js)
        resistance:           ov.resistance           ?? phase.resistance,
        enemy_strength_ratio: ov.enemy_strength_ratio ?? phase.enemy_strength_ratio,
        maneuver_group:       ov.maneuver_group       ?? phase.maneuver_group,
        special_condition:    ov.special_condition    ?? phase.special_condition,
        foothold_day:         ov.foothold_day         ?? phase.foothold_day,
        weather:              ov.weather              ?? phase.weather,   // R2-18
        advance_km_day:     ov.advance_km_day     ?? phase.advance_km_day,   // R2-30: advance of the brigade in this phase, km per day
        // V4-013: propagate sub_phases so cascade to brigades carries them through
        sub_phases:          ov.sub_phases           ?? phase.sub_phases ?? [],
      };
    });

  /** Seed override from current corps phase values, then commit to local state. */
  const addOverride = (index, phase) => {
    setPhaseOverrides(prev => ({
      ...prev,
      [index]: {
        operational_posture: phase.operational_posture,
        mission_type:        phase.mission_type,
        description:         phase.description || ''
      }
    }));
  };

  /** Update a single field inside an existing override. */
  const updateOverride = (index, field, value) => {
    setPhaseOverrides(prev => {
      const existing = prev[index] || {};
      const updated = { ...existing, [field]: value };
      if (field === 'operational_posture') {
        updated.mission_type = POSTURE_TO_MISSION[value] || 'DEFEND';
      }
      return { ...prev, [index]: updated };
    });
  };

  /** Remove override — reverts to inherited corps value. */
  const removeOverride = (index) => {
    setPhaseOverrides(prev => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  // ── V4-013: Sub-phase management ─────────────────────────────────────────

  const toggleSubPhaseExpand = (phaseIdx) => {
    setExpandedSubPhases(prev => ({ ...prev, [phaseIdx]: !prev[phaseIdx] }));
  };

  const _getSubPhases = (phaseIdx, isInherited) => {
    if (isInherited) return phaseOverrides[phaseIdx]?.sub_phases || [];
    return phases[phaseIdx]?.sub_phases || [];
  };

  const _setSubPhases = (phaseIdx, newSPs, isInherited) => {
    if (isInherited) {
      updateOverride(phaseIdx, 'sub_phases', newSPs);
    } else {
      updatePhase(phaseIdx, 'sub_phases', newSPs);
    }
  };

  const initSubPhases = (phaseIdx, phaseDuration, defaultMission, isInherited) => {
    const norm = defaultMission || 'SUPPORTING';
    const OP_TYPE_ONLY = ['URBAN', 'RIVER_CROSSING'];
    _setSubPhases(phaseIdx, [{
      sub_phase_number: 1,
      duration_days: phaseDuration,
      role:           OP_TYPE_ONLY.includes(norm) ? 'SUPPORTING' : norm,
      operation_type: OP_TYPE_ONLY.includes(norm) ? norm : null,
      mission_type:   norm, // BC field
      terrain: null
    }], isInherited);
    setExpandedSubPhases(prev => ({ ...prev, [phaseIdx]: true }));
  };

  const addSubPhase = (phaseIdx, defaultMission, isInherited) => {
    const current = _getSubPhases(phaseIdx, isInherited);
    const norm = defaultMission || 'SUPPORTING';
    const OP_TYPE_ONLY = ['URBAN', 'RIVER_CROSSING'];
    _setSubPhases(phaseIdx, [
      ...current,
      {
        sub_phase_number: current.length + 1,
        duration_days: 1,
        role:           OP_TYPE_ONLY.includes(norm) ? 'SUPPORTING' : norm,
        operation_type: OP_TYPE_ONLY.includes(norm) ? norm : null,
        mission_type:   norm, // BC field
        terrain: null
      }
    ], isInherited);
  };

  const removeSubPhase = (phaseIdx, spIdx, isInherited) => {
    const updated = _getSubPhases(phaseIdx, isInherited)
      .filter((_, i) => i !== spIdx)
      .map((sp, i) => ({ ...sp, sub_phase_number: i + 1 }));
    _setSubPhases(phaseIdx, updated, isInherited);
  };

  // OBS-S102-007: multi-field update for atomic role + operation_type changes
  const updateSubPhaseFields = (phaseIdx, spIdx, fields, isInherited) => {
    const updated = _getSubPhases(phaseIdx, isInherited)
      .map((sp, i) => i === spIdx ? { ...migrateSubPhase(sp), ...fields } : migrateSubPhase(sp));
    _setSubPhases(phaseIdx, updated, isInherited);
  };

  const disableSubPhases = (phaseIdx, isInherited) => {
    _setSubPhases(phaseIdx, [], isInherited);
    setExpandedSubPhases(prev => ({ ...prev, [phaseIdx]: false }));
  };

  const renderSubPhasePanel = (phaseIdx, phaseDuration, parentMission, isInherited) => {
    const sps = _getSubPhases(phaseIdx, isInherited);
    const hasSubPhases = sps.length > 0;
    const sumDays = sps.reduce((s, sp) => s + (sp.duration_days || 0), 0);
    const sumValid = sumDays === phaseDuration;

    return (
      <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, mt: 0.5, mb: 0.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="caption" fontWeight="bold" color="text.secondary">
            Tactical sub-phases — {phaseDuration} day{phaseDuration !== 1 ? 's' : ''} to allocate
          </Typography>
          {hasSubPhases ? (
            <Stack direction="row" spacing={0.5}>
              <Button size="small" startIcon={<AddIcon />} variant="outlined"
                onClick={() => addSubPhase(phaseIdx, parentMission, isInherited)}>
                Add
              </Button>
              <Button size="small" color="warning" variant="text"
                onClick={() => disableSubPhases(phaseIdx, isInherited)}>
                Disable
              </Button>
            </Stack>
          ) : (
            <Button size="small" startIcon={<AddIcon />} variant="contained" color="primary"
              onClick={() => initSubPhases(phaseIdx, phaseDuration, parentMission, isInherited)}>
              Enable Sub-phases
            </Button>
          )}
        </Stack>

        {hasSubPhases && (
          <>
            <Chip
              color={sumValid ? 'success' : 'error'}
              size="small"
              label={sumValid ? `${sumDays}d allocated — valid` : `${sumDays}d / ${phaseDuration}d — must match`}
              sx={{ mb: 1 }}
            />
            {sps.map((sp, spIdx) => {
              // OBS-S102-007: migrate old single-field sub-phases on display
              const msp = migrateSubPhase(sp);
              // 2026-10-13: the factor shown is the casualty table factor at moderate resistance (form x condition x group), not the old multiplier
              const combined = (() => {
                try {
                  return resolvePhaseProfile({ posture: 'DELIBERATE_DEFENSE', subPhase: msp, dayInPhase: 1, resistanceIndex: 1 }).total_factor;
                } catch (e) {
                  return null;
                }
              })();
              const showCombined = msp.operation_type != null && combined !== null;
              return (
              <Box key={spIdx} sx={{ mb: 0.75, p: 0.75, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="caption" sx={{ minWidth: 28, fontWeight: 'bold', color: 'primary.main' }}>
                    SP{msp.sub_phase_number}
                  </Typography>
                  <TextField
                    type="number"
                    size="small"
                    label="Days"
                    value={msp.duration_days}
                    onChange={(e) => updateSubPhaseFields(phaseIdx, spIdx, { duration_days: Math.max(1, parseInt(e.target.value, 10) || 1) }, isInherited)}
                    sx={{ width: 72 }}
                    inputProps={{ min: 1 }}
                  />
                  {/* Selector 1: Role */}
                  <FormControl size="small" sx={{ minWidth: 195 }}>
                    <InputLabel sx={{ fontSize: '0.75rem' }}>Role</InputLabel>
                    <Select
                      label="Role"
                      value={msp.role ?? 'SUPPORTING'}
                      onChange={(e) => updateSubPhaseFields(phaseIdx, spIdx, {
                        role: e.target.value,
                        mission_type: e.target.value
                      }, isInherited)}
                    >
                      <MenuItem value="MAIN_EFFORT">Main Effort (frontal)</MenuItem>
                      <MenuItem value="SUPPORTING">Supporting (frontal)</MenuItem>
                      <MenuItem value="EXPLOITATION">Exploitation (flank / indirect ×0.75)</MenuItem>
                      <MenuItem value="ECONOMY_OF_FORCE">Economy of Force (limited objective ×0.60)</MenuItem>
                      <MenuItem value="FEINT">Feint / Demonstration (×0.40)</MenuItem>
                      <MenuItem value="FIXING">Fixing (×0.40)</MenuItem>
                      <MenuItem value="RESERVE">Reserve (near zero)</MenuItem>
                    </Select>
                  </FormControl>
                  {/* Selector 2: Operation Type */}
                  <FormControl size="small" sx={{ minWidth: 210 }}>
                    <InputLabel sx={{ fontSize: '0.75rem' }}>Operation Type</InputLabel>
                    <Select
                      label="Operation Type"
                      value={msp.operation_type ?? ''}
                      onChange={(e) => updateSubPhaseFields(phaseIdx, spIdx, { operation_type: e.target.value || null }, isInherited)}
                      displayEmpty
                    >
                      <MenuItem value=""><em>(none — inherits phase posture)</em></MenuItem>
                      <ListSubheader sx={{ fontSize: '0.7rem', lineHeight: '24px' }}>Offensive</ListSubheader>
                      <MenuItem value="DELIBERATE_ATTACK">Deliberate Attack</MenuItem>
                      <MenuItem value="HASTY_ATTACK">Hasty Attack</MenuItem>
                      <MenuItem value="MOVEMENT_TO_CONTACT">Movement to Contact</MenuItem>
                      <MenuItem value="URBAN">Urban Operations (MOUT)</MenuItem>
                      <MenuItem value="RIVER_CROSSING">River Crossing (assault, then bridgehead)</MenuItem>
                      <MenuItem value="EXPLOITATION">Exploitation</MenuItem>
                      <MenuItem value="PURSUIT">Pursuit</MenuItem>
                      <MenuItem value="AIRBORNE_ASSAULT">Airborne / Air Assault (until link-up)</MenuItem>
                      <ListSubheader sx={{ fontSize: '0.7rem', lineHeight: '24px' }}>Defensive</ListSubheader>
                      <MenuItem value="DEFEND">Defend (Prepared)</MenuItem>
                      <MenuItem value="HASTY_DEFENSE">Hasty Defense</MenuItem>
                      <MenuItem value="MOBILE_DEFENSE">Mobile Defense</MenuItem>
                      <MenuItem value="DELAY">Delay</MenuItem>
                      <MenuItem value="WITHDRAWAL">Withdrawal</MenuItem>
                      <MenuItem value="RETIREMENT">Retirement</MenuItem>
                      <ListSubheader sx={{ fontSize: '0.7rem', lineHeight: '24px' }}>Movement</ListSubheader>
                      <MenuItem value="TACTICAL_MARCH">Tactical March</MenuItem>
                      <MenuItem value="ADMINISTRATIVE_MARCH">Administrative March</MenuItem>
                    </Select>
                  </FormControl>
                  {/* Selector 3: Terrain */}
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel sx={{ fontSize: '0.75rem' }}>Terrain</InputLabel>
                    <Select
                      label="Terrain"
                      value={msp.terrain ?? ''}
                      onChange={(e) => updateSubPhaseFields(phaseIdx, spIdx, { terrain: e.target.value || null }, isInherited)}
                      displayEmpty
                    >
                      <MenuItem value=""><em>(Scenario terrain)</em></MenuItem>
                      <MenuItem value="PLAINS">Plains / Flatland</MenuItem>
                      <MenuItem value="ROLLING_HILLS">Rolling Hills</MenuItem>
                      <MenuItem value="MOUNTAIN">Mountainous</MenuItem>
                      <MenuItem value="FOREST">Forested</MenuItem>
                      <MenuItem value="URBAN">Urban / City</MenuItem>
                      <MenuItem value="URBAN_COMBAT">Urban Combat Zone</MenuItem>
                      <MenuItem value="DESERT">Desert / Arid</MenuItem>
                      <MenuItem value="SWAMP_WETLAND">Swamp / Wetland</MenuItem>
                      <MenuItem value="COASTAL">Coastal / Littoral</MenuItem>
                    </Select>
                  </FormControl>
                  {showCombined && (
                    <Chip
                      size="small"
                      label={`Table factor ×${combined.toFixed(2)} (moderate)`}
                      color={combined >= 2.0 ? 'error' : combined >= 1.3 ? 'warning' : combined <= 0.5 ? 'info' : 'default'}
                      variant="outlined"
                      sx={{ fontSize: '0.65rem', height: 20 }}
                    />
                  )}
                  <IconButton size="small" color="error" sx={{ ml: 'auto' }}
                    onClick={() => removeSubPhase(phaseIdx, spIdx, isInherited)}
                    disabled={sps.length <= 1}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>
              );
            })}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              V4-013: Multipliers stack on division tactical role. Applied per-day in calculator.
            </Typography>
          </>
        )}
      </Box>
    );
  };

  // ── Validation ─────────────────────────────────────────────────────────────

  const totalPhaseDays = phases.reduce((sum, p) => sum + p.duration_days, 0);
  const phaseDaysValid   = totalPhaseDays === operationDurationDays;
  const phaseDaysError   = totalPhaseDays > operationDurationDays;
  const phaseDaysWarning = totalPhaseDays < operationDurationDays && phases.length > 0;

  const overrideCount = Object.keys(phaseOverrides).length;

  // ── Save handler ───────────────────────────────────────────────────────────

  const handleSave = () => {
    // ENH-023: when inheriting, pass effective phases (overrides applied) so cascade
    // to brigades propagates correct effective values. Also pass phase_overrides dict
    // so Tab2 can re-apply overrides if corps phases change and cascade re-runs.
    const effectivePhases = usesCorpsPhases
      ? calculatePhaseDays(buildEffectivePhases(corpsPhases, phaseOverrides))
      : phases;

    onSave(initialDivision.division_id, {
      personnel,
      operational_state:  operationalState,
      position_feba_km:   positionFeba,
      uses_corps_phases:  usesCorpsPhases,
      phases:             effectivePhases,
      phase_overrides:    usesCorpsPhases ? phaseOverrides : {}
    });
  };

  // Brigade counts for info display
  const divisionBrigades   = initialDivision
    ? brigades.filter(b => b.parent_division_id === initialDivision.division_id)
    : [];
  const inheritingBrigades = divisionBrigades.filter(b => b.uses_division_phases);

  // Guard: Don't render dialog if no division provided
  if (!initialDivision) return null;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h5">
            {MILITARY_ICONS.BATTALION} Division {initialDivision.division_number} Configuration
          </Typography>
          <Chip
            label={
              usesCorpsPhases
                ? (overrideCount > 0
                    ? `${overrideCount} Override${overrideCount > 1 ? 's' : ''}`
                    : 'Inheriting from Corps')
                : 'Custom Phases'
            }
            color={usesCorpsPhases ? (overrideCount > 0 ? 'warning' : 'success') : 'warning'}
            size="small"
          />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {initialDivision.division_name}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>{STATUS_ICONS.INFO} Division Phase Control</AlertTitle>
          Divisions can inherit phases from Corps or define custom phases for special missions.
          When inheriting, individual phases can be overridden (posture/mission/description)
          while keeping the corps timeline structure. Duration cannot be changed via override.
          Changes cascade to {inheritingBrigades.length} of {divisionBrigades.length} brigades.
        </Alert>

        {/* Division HQ Configuration */}
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          Division HQ Configuration
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Division HQ Personnel"
              type="number"
              value={personnel}
              InputProps={{
                readOnly: true,
                endAdornment: <InputAdornment position="end">troops</InputAdornment>
              }}
              helperText="Configured in Tab 1 — modify there"
              sx={{ '& .MuiInputBase-input': { color: 'text.secondary' } }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth>
              <InputLabel>Operational State</InputLabel>
              <Select
                value={operationalState}
                onChange={(e) => setOperationalState(e.target.value)}
                label="Operational State"
              >
                <MenuItem value="SUPPORT_ACTIVE">Support Active</MenuItem>
                <MenuItem value="TRUE_RESERVE">True Reserve</MenuItem>
                <MenuItem value="COMBAT_ENGAGED">Combat Engaged</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="body2" gutterBottom>
              Position from FEBA: {positionFeba} km
            </Typography>
            <Slider
              value={positionFeba}
              onChange={(e, newValue) => setPositionFeba(newValue)}
              min={10}
              max={60}
              step={5}
              marks={[
                { value: 10, label: '10km' },
                { value: 30, label: '30km' },
                { value: 60, label: '60km' }
              ]}
              valueLabelDisplay="auto"
            />
            <Typography variant="caption" color="text.secondary">
              Division HQ typically 20–40km behind FEBA
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Phase Inheritance Toggle */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={usesCorpsPhases}
                onChange={(e) => handleToggleInheritance(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Stack>
                <Typography variant="subtitle1">
                  {usesCorpsPhases
                    ? `${STATUS_ICONS.GREEN_CHECK} Inherit Phases from Corps`
                    : `${UI_ICONS.SETTINGS} Use Custom Division Phases`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {usesCorpsPhases
                    ? 'This division follows Corps operational phases'
                    : 'This division has independent operational phases'}
                </Typography>
              </Stack>
            }
          />
        </Box>

        {/* Phase Display/Editor */}
        {usesCorpsPhases ? (

          // ── INHERITED PHASES — read-only except for per-phase overrides (ENH-023)
          <Box>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
              <Typography variant="h6">Inherited Phases from Corps</Typography>
              <Chip label={`${corpsPhases.length} phases`} size="small" color="success" />
              {overrideCount > 0 && (
                <Chip
                  label={`${overrideCount} overridden`}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              )}
            </Stack>

            {corpsPhases.length === 0 ? (
              <Alert severity="info">
                Corps has not defined any phases yet. This division will automatically receive
                phases when Corps configures them.
              </Alert>
            ) : (
              <>
                <Alert severity="info" sx={{ mb: 2 }} icon={false}>
                  <Typography variant="body2">
                    Click <strong>Override</strong> on any row to change posture, mission, or
                    description for that phase only. Duration is locked to the corps timeline.
                    Click <strong>Reset</strong> to revert to the inherited value.
                  </Typography>
                </Alert>

                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Phase</TableCell>
                        <TableCell>Duration</TableCell>
                        <TableCell>Days</TableCell>
                        <TableCell>Posture</TableCell>
                        <TableCell>Mission</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {corpsPhases.map((phase, index) => {
                        const isOverridden = Boolean(phaseOverrides[index]);
                        const ov = phaseOverrides[index] || {};
                        return (
                          <React.Fragment key={index}>
                          <TableRow
                            sx={{ bgcolor: isOverridden ? 'warning.50' : 'inherit' }}
                          >
                            {/* Phase chip */}
                            <TableCell>
                              <Chip
                                label={`Phase ${phase.phase_number}`}
                                size="small"
                                color={isOverridden ? 'warning' : 'default'}
                              />
                            </TableCell>
                            {/* Duration — always locked */}
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {phase.duration_days} days
                              </Typography>
                            </TableCell>
                            {/* Start–End */}
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {phase.start_day}–{phase.end_day}
                              </Typography>
                            </TableCell>
                            {/* Posture — editable when overridden */}
                            <TableCell>
                              {isOverridden ? (
                                <FormControl size="small" sx={{ minWidth: 160 }}>
                                  <Select
                                    value={ov.operational_posture}
                                    onChange={(e) =>
                                      updateOverride(index, 'operational_posture', e.target.value)
                                    }
                                  >
                                    <MenuItem value="DELIBERATE_ATTACK">Deliberate Attack</MenuItem>
                                    <MenuItem value="HASTY_ATTACK">Hasty Attack</MenuItem>
                                    <MenuItem value="EXPLOITATION">Exploitation</MenuItem>
                                    <MenuItem value="DELIBERATE_DEFENSE">Deliberate Defense</MenuItem>
                                    <MenuItem value="HASTY_DEFENSE">Hasty Defense</MenuItem>
                                    <MenuItem value="MOVEMENT_TO_CONTACT">Movement to Contact</MenuItem>
                                    <MenuItem value="PURSUIT">Pursuit</MenuItem>
                                    <MenuItem value="MOBILE_DEFENSE">Mobile Defense</MenuItem>
                                    <MenuItem value="DELAY">Delay</MenuItem>
                                    <MenuItem value="WITHDRAWAL">Withdrawal</MenuItem>
                                    <MenuItem value="RETIREMENT">Retirement (out of contact)</MenuItem>
                                    <MenuItem value="CONSOLIDATION">Consolidation</MenuItem>
                                    <MenuItem value="RESERVE">Reserve</MenuItem>
                                    <MenuItem value="MARCH_TACTICAL">
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                        Tactical March
                                        <Tooltip title="Combat-ready movement. High security, slow speed." arrow placement="right">
                                          <InfoIcon fontSize="small" color="action" />
                                        </Tooltip>
                                      </Box>
                                    </MenuItem>
                                    <MenuItem value="MARCH_ADMINISTRATIVE">
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                        Administrative March
                                        <Tooltip title="Fast movement in safe rear areas. Low combat readiness." arrow placement="right">
                                          <InfoIcon fontSize="small" color="action" />
                                        </Tooltip>
                                      </Box>
                                    </MenuItem>
                                    <MenuItem value="TRANSIT">
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                        Transit
                                        <Tooltip title="Strategic theater-to-theater movement. Minimal combat readiness." arrow placement="right">
                                          <InfoIcon fontSize="small" color="action" />
                                        </Tooltip>
                                      </Box>
                                    </MenuItem>
                                  </Select>
                                </FormControl>
                              ) : (
                                <Typography variant="body2">{phase.operational_posture}</Typography>
                              )}
                            </TableCell>
                            {/* Mission — editable when overridden */}
                            <TableCell>
                              {isOverridden ? (
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                  <Select
                                    value={ov.mission_type}
                                    onChange={(e) =>
                                      updateOverride(index, 'mission_type', e.target.value)
                                    }
                                  >
                                    <MenuItem value="ATTACK">Attack</MenuItem>
                                    <MenuItem value="DEFEND">Defend</MenuItem>
                                    <MenuItem value="URBAN">🏙️ Urban Operations</MenuItem>
                                    <MenuItem value="RIVER_CROSSING">🌊 River Crossing</MenuItem>
                                    <MenuItem value="EXPLOITATION">Exploitation</MenuItem>
                                    <MenuItem value="CONSOLIDATION">Consolidation</MenuItem>
                                    <MenuItem value="RESERVE">Reserve</MenuItem>
                                    <MenuItem value="MOVEMENT">
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                        Movement
                                        <Tooltip title="All march postures map to MOVEMENT mission." arrow placement="right">
                                          <InfoIcon fontSize="small" color="action" />
                                        </Tooltip>
                                      </Box>
                                    </MenuItem>
                                  </Select>
                                </FormControl>
                              ) : (
                                <Typography variant="body2">{phase.mission_type}</Typography>
                              )}
                            </TableCell>
                            {/* Description — editable when overridden */}
                            <TableCell>
                              {isOverridden ? (
                                <TextField
                                  size="small"
                                  value={ov.description ?? phase.description ?? ''}
                                  onChange={(e) =>
                                    updateOverride(index, 'description', e.target.value)
                                  }
                                  placeholder="Phase description"
                                  sx={{ minWidth: 180 }}
                                />
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  {phase.description || '–'}
                                </Typography>
                              )}
                            </TableCell>
                            {/* Override / Reset + Sub-phase toggle */}
                            <TableCell align="right">
                              <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                                {isOverridden && (
                                  <IconButton
                                    size="small"
                                    onClick={() => toggleSubPhaseExpand(index)}
                                    color={_getSubPhases(index, true).length > 0 ? 'primary' : 'default'}
                                    title="Toggle sub-phase editor"
                                  >
                                    <ExpandMoreIcon
                                      sx={{
                                        transform: expandedSubPhases[index] ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.2s'
                                      }}
                                    />
                                  </IconButton>
                                )}
                                {isOverridden ? (
                                  <Button
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    onClick={() => removeOverride(index)}
                                  >
                                    Reset
                                  </Button>
                                ) : (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => addOverride(index, phase)}
                                  >
                                    Override
                                  </Button>
                                )}
                              </Stack>
                            </TableCell>
                          </TableRow>
                          {/* V5-013: Division role row — only when corps echelon (corpsPhases present) and phase overridden */}
                          {isOverridden && corpsPhases.length > 0 && (
                            <TableRow key={`${index}-divrole`}>
                              <TableCell
                                colSpan={7}
                                sx={{ pt: 0.5, pb: 0.5, bgcolor: 'warning.50', borderBottom: 'none' }}
                              >
                                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 102, fontWeight: 500 }}>
                                    Division Role:
                                  </Typography>
                                  {DIVISION_ROLE_OPTIONS.map(opt => {
                                    const selected = (ov.division_role ?? null) === opt.value;
                                    return (
                                      <Chip
                                        key={opt.value}
                                        label={opt.label}
                                        size="small"
                                        color={selected ? opt.color : 'default'}
                                        variant={selected ? 'filled' : 'outlined'}
                                        onClick={() => updateOverride(index, 'division_role', selected ? null : opt.value)}
                                        sx={{ cursor: 'pointer', fontWeight: selected ? 'bold' : 'normal' }}
                                      />
                                    );
                                  })}
                                  {ov.division_role && (
                                    <Chip
                                      label="Clear"
                                      size="small"
                                      variant="outlined"
                                      onClick={() => updateOverride(index, 'division_role', null)}
                                      sx={{ cursor: 'pointer' }}
                                    />
                                  )}
                                </Stack>
                              </TableCell>
                            </TableRow>
                          )}
                          {/* 2026-10-13: casualty profile row (resistance, enemy ratio, maneuver group, special condition) */}
                          {isOverridden && (
                            <TableRow key={`${index}-profile`}>
                              <TableCell colSpan={7} sx={{ pt: 0.5, pb: 0.75, bgcolor: 'warning.50', borderBottom: 'none' }}>
                                <PhaseCasualtyControls
                                  phase={{ ...phase, ...ov }}
                                  onChange={(field, value) => updateOverride(index, field, value)}
                                />
                              </TableCell>
                            </TableRow>
                          )}
                          {/* V4-013: Sub-phase editor row */}
                          {isOverridden && (
                            <TableRow key={`${index}-sp`}>
                              <TableCell
                                colSpan={7}
                                sx={{ pt: 0, pb: expandedSubPhases[index] ? 1 : 0, borderBottom: expandedSubPhases[index] ? undefined : 'none' }}
                              >
                                <Collapse in={Boolean(expandedSubPhases[index])} timeout="auto" unmountOnExit>
                                  {renderSubPhasePanel(index, phase.duration_days, ov.mission_type || phase.mission_type, true)}
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Timeline — overridden phases shown with darker/outlined bar */}
                <Box sx={{ p: 2, bgcolor: 'success.50', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Timeline (Total: {operationDurationDays} days)
                    {overrideCount > 0 && ' — outlined bars have division overrides'}
                  </Typography>
                  <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
                    {corpsPhases.map((phase, index) => {
                      const isOverridden = Boolean(phaseOverrides[index]);
                      return (
                        <Box
                          key={index}
                          sx={{
                            flex: phase.duration_days,
                            height: 40,
                            bgcolor: isOverridden
                              ? `hsl(${index * 60}, 80%, 42%)`
                              : `hsl(${index * 60}, 70%, 60%)`,
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            outline: isOverridden ? '2px solid #f57c00' : 'none',
                            outlineOffset: '1px',
                          }}
                        >
                          P{phase.phase_number}
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              </>
            )}
          </Box>

        ) : (

          // ── CUSTOM PHASES — Editable
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">Custom Division Phases</Typography>
              <Button startIcon={<AddIcon />} onClick={addPhase} variant="outlined" size="small">
                Add Phase
              </Button>
            </Stack>

            {/* BUG-077 companion (March 13, 2026): Division phase mismatch warning */}
            {(() => {
              const mismatches = phases.filter(
                p => (POSTURE_TO_MISSION[p.operational_posture] || 'DEFEND') !== p.mission_type
              );
              if (mismatches.length === 0) return null;
              return (
                <Alert
                  severity="warning"
                  sx={{ mb: 2 }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setPhases(calculatePhaseDays(
                          phases.map(p => ({
                            ...p,
                            mission_type: POSTURE_TO_MISSION[p.operational_posture] || 'DEFEND'
                          }))
                        ));
                      }}
                    >
                      Fix All
                    </Button>
                  }
                >
                  <AlertTitle>[!] Phase Mismatch ({mismatches.length})</AlertTitle>
                  {mismatches.map((p, i) => (
                    <Typography key={i} variant="body2">
                      Phase {p.phase_number}: {p.operational_posture} expects{' '}
                      <strong>{POSTURE_TO_MISSION[p.operational_posture] || 'DEFEND'}</strong>,
                      has <strong>{p.mission_type}</strong>
                    </Typography>
                  ))}
                </Alert>
              );
            })()}

            {phases.length === 0 ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                No custom phases configured. Add phases to define this division&apos;s timeline.
              </Alert>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Phase</TableCell>
                        <TableCell>Duration</TableCell>
                        <TableCell>Days</TableCell>
                        <TableCell>Posture</TableCell>
                        <TableCell>Mission</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {phases.map((phase, index) => (
                        <React.Fragment key={index}>
                        <TableRow>
                          <TableCell>
                            <Chip label={`Phase ${phase.phase_number}`} size="small" color="warning" />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={phase.duration_days}
                              onChange={(e) => updatePhase(index, 'duration_days', Number(e.target.value))}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">days</InputAdornment>
                              }}
                              sx={{ width: 100 }}
                              inputProps={{ min: 1 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {phase.start_day}–{phase.end_day}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                              <Select
                                value={phase.operational_posture}
                                onChange={(e) => updatePhase(index, 'operational_posture', e.target.value)}
                              >
                                <MenuItem value="DELIBERATE_ATTACK">Deliberate Attack</MenuItem>
                                <MenuItem value="HASTY_ATTACK">Hasty Attack</MenuItem>
                                <MenuItem value="EXPLOITATION">Exploitation</MenuItem>
                                <MenuItem value="DELIBERATE_DEFENSE">Deliberate Defense</MenuItem>
                                <MenuItem value="HASTY_DEFENSE">Hasty Defense</MenuItem>
                                <MenuItem value="MOVEMENT_TO_CONTACT">Movement to Contact</MenuItem>
                                <MenuItem value="PURSUIT">Pursuit</MenuItem>
                                <MenuItem value="MOBILE_DEFENSE">Mobile Defense</MenuItem>
                                <MenuItem value="DELAY">Delay</MenuItem>
                                <MenuItem value="WITHDRAWAL">Withdrawal</MenuItem>
                                <MenuItem value="RETIREMENT">Retirement (out of contact)</MenuItem>
                                <MenuItem value="CONSOLIDATION">Consolidation</MenuItem>
                                <MenuItem value="RESERVE">Reserve</MenuItem>
                                <MenuItem value="MARCH_TACTICAL">
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                    Tactical March
                                    <Tooltip
                                      title="Combat-ready movement. High security, slow speed. Can engage enemy immediately. Use when enemy contact possible. Battle rate: 0.1%/day"
                                      arrow placement="right"
                                    >
                                      <InfoIcon fontSize="small" color="action" />
                                    </Tooltip>
                                  </Box>
                                </MenuItem>
                                <MenuItem value="MARCH_ADMINISTRATIVE">
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                    Administrative March
                                    <Tooltip
                                      title="Fast movement in safe rear areas. Low combat readiness. Needs 30-60 min to reorganize for combat. Battle rate: 0.01%/day"
                                      arrow placement="right"
                                    >
                                      <InfoIcon fontSize="small" color="action" />
                                    </Tooltip>
                                  </Box>
                                </MenuItem>
                                <MenuItem value="TRANSIT">
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                    Transit
                                    <Tooltip
                                      title="Strategic theater-to-theater movement. Minimal combat readiness. Very secure areas only. Battle rate: 0.005%/day"
                                      arrow placement="right"
                                    >
                                      <InfoIcon fontSize="small" color="action" />
                                    </Tooltip>
                                  </Box>
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <Select
                                value={phase.mission_type}
                                onChange={(e) => updatePhase(index, 'mission_type', e.target.value)}
                              >
                                <MenuItem value="ATTACK">Attack</MenuItem>
                                <MenuItem value="DEFEND">Defend</MenuItem>
                                <MenuItem value="URBAN">🏙️ Urban Operations</MenuItem>
                                <MenuItem value="RIVER_CROSSING">🌊 River Crossing</MenuItem>
                                <MenuItem value="EXPLOITATION">Exploitation</MenuItem>
                                <MenuItem value="CONSOLIDATION">Consolidation</MenuItem>
                                <MenuItem value="RESERVE">Reserve</MenuItem>
                                <MenuItem value="MOVEMENT">
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                    Movement
                                    <Tooltip
                                      title="All march postures map to MOVEMENT mission. Medical facilities offline during movement."
                                      arrow placement="right"
                                    >
                                      <InfoIcon fontSize="small" color="action" />
                                    </Tooltip>
                                  </Box>
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={phase.description || ''}
                              onChange={(e) => updatePhase(index, 'description', e.target.value)}
                              placeholder="Phase description"
                              sx={{ minWidth: 200 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                              <IconButton
                                size="small"
                                onClick={() => toggleSubPhaseExpand(index)}
                                color={_getSubPhases(index, false).length > 0 ? 'primary' : 'default'}
                                title="Toggle sub-phase editor"
                              >
                                <ExpandMoreIcon
                                  sx={{
                                    transform: expandedSubPhases[index] ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s'
                                  }}
                                />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => deletePhase(index)}
                                disabled={phases.length === 1}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                        {/* V4-013: Sub-phase editor row */}
                        <TableRow key={`${index}-sp`}>
                          <TableCell
                            colSpan={7}
                            sx={{ pt: 0, pb: expandedSubPhases[index] ? 1 : 0, borderBottom: expandedSubPhases[index] ? undefined : 'none' }}
                          >
                            <Collapse in={Boolean(expandedSubPhases[index])} timeout="auto" unmountOnExit>
                              {renderSubPhasePanel(index, phase.duration_days, phase.mission_type, false)}
                            </Collapse>
                          </TableCell>
                        </TableRow>
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Validation */}
                {phaseDaysValid && (
                  <Alert severity="success" icon={<CheckCircleIcon />}>
                    {STATUS_ICONS.GREEN_CHECK} All {operationDurationDays} days covered by phases.
                  </Alert>
                )}
                {phaseDaysWarning && (
                  <Alert severity="warning" icon={<WarningIcon />}>
                    {STATUS_ICONS.YELLOW_WARNING} Phases cover {totalPhaseDays} days, but operation
                    is {operationDurationDays} days. Add {operationDurationDays - totalPhaseDays} more
                    day(s).
                  </Alert>
                )}
                {phaseDaysError && (
                  <Alert severity="error" icon={<WarningIcon />}>
                    {STATUS_ICONS.RED_X} Phases total {totalPhaseDays} days, exceeding operation
                    duration ({operationDurationDays} days). Reduce by{' '}
                    {totalPhaseDays - operationDurationDays} day(s).
                  </Alert>
                )}
              </>
            )}
          </Box>
        )}

        {/* Brigade Inheritance Status */}
        {divisionBrigades.length > 0 && (
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              {MILITARY_ICONS.BATTALION} Brigades in this Division
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {divisionBrigades.map(brig => (
                <Chip
                  key={brig.brigade_id}
                  label={brig.brigade_name || `Brigade ${brig.brigade_number}`}
                  color={brig.uses_division_phases ? 'success' : 'default'}
                  size="small"
                  icon={brig.uses_division_phases ? <CheckCircleIcon /> : <InfoIcon />}
                />
              ))}
            </Stack>
            {inheritingBrigades.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {inheritingBrigades.length} of {divisionBrigades.length} brigade(s) will receive
                these phases
              </Typography>
            )}
            {divisionBrigades.filter(b => !b.uses_division_phases).length > 0 && (
              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                {divisionBrigades.filter(b => !b.uses_division_phases).length} brigade(s) have
                custom phases (not affected)
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!usesCorpsPhases && !phaseDaysValid && phases.length > 0}
        >
          Save &amp; Cascade to Brigades
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DivisionConfigDialog;

/**
 * INTEGRATION INSTRUCTIONS FOR Tab2_Operations.jsx:
 *
 * 1. Import this component at the top:
 *    import DivisionConfigDialog from './DivisionConfigDialog';
 *
 * 2. Add the component before the closing return statement:
 *    <DivisionConfigDialog
 *      open={editingDivision !== null}
 *      onClose={handleCloseDivisionDialog}
 *      onSave={handleSaveDivisionConfig}
 *      initialDivision={divisionConfigs.find(d => d.division_id === editingDivision)}
 *      corpsPhases={corpsPhases}
 *      operationDurationDays={totalDuration}
 *      brigades={brigadeConfigs}
 *      onToggleInheritance={toggleDivisionInheritance}
 *    />
 *
 * 3. ENH-023 (Tab2 v7.13.0 requirements):
 *    - Division init must include phase_overrides: {} at all init paths
 *    - handleSaveDivisionConfig receives phase_overrides in config and must persist it
 *    - cascadeCorpsPhasesToDivisions must call getEffectivePhases(div, corpsPhases)
 *      when updating division.phases so overrides survive re-cascades from corps
 */
